/**
 * What the inline editors on Brand Guidelines' page share (ORB-971, ORB-973). An entry's patch, when it has changed and
 * where a refusal lands are the console model's rules, so the console and this page edit a brand the same way.
 */

import type { RefObject } from 'preact'
import { useEffect, useRef, useState } from 'preact/hooks'
import {
  FORM_ERROR,
  editorErrors,
  editorName,
  editorOpener,
  editorPatch,
  inWords,
  openTokenEditor,
  openerOf,
  removalPatch,
  type TokenEditor,
  type TokenKind,
  type TokenPatch,
  type TokenRef,
} from '@/components/console/knowledgeBase/brandGuidelinesModel'
import type { WriteOutcome } from './useBrandGuidelines'

/** The roles that point at each token or style, by its name. */
export type Pointers = Map<string, string[]>

/** One write of an entry: null when another write is still running. */
export type SaveEntry = (patch: TokenPatch) => Promise<WriteOutcome | null>

/** Said beside Save when a role reads what the form changes. */
export function roleNote(roles: readonly string[] | undefined, noun: string): string | null {
  if (!roles || roles.length === 0) return null
  return `${inWords(roles)} ${roles.length === 1 ? 'uses' : 'use'} this ${noun}, so emails and pages change with it.`
}

const sameEntry = (ref: TokenRef | null, kind: TokenKind, name: string) => ref !== null && ref.kind === kind && ref.name === name

export type TokenEditing = ReturnType<typeof useTokenEditor>

/**
 * The page's inline editor, and the question asked before an entry is removed. One entry is open at a time, and a
 * refusal stays beside what it is about. A closed editor or question takes focus with it, so focus goes back to the
 * button that opened it unless the person has moved on; after a rename or a removal that button is gone, so it goes to
 * the entry's new Change, or the Add of its group or section.
 */
export function useTokenEditor(container: RefObject<HTMLElement>, onSave: SaveEntry) {
  const [editor, setEditor] = useState<TokenEditor | null>(null)
  const [asking, setAsking] = useState<TokenRef | null>(null)
  const [refusal, setRefusal] = useState<{ ref: TokenRef; message: string } | null>(null)

  const opener = editor !== null ? editorOpener(editor) : asking !== null ? openerOf('remove', asking.kind, asking.name) : null
  const lastOpener = useRef<string | null>(null)
  const returnTo = useRef<string[]>([])
  useEffect(() => {
    const closed = lastOpener.current
    lastOpener.current = opener
    const fallbacks = returnTo.current
    returnTo.current = []
    if (opener !== null || closed === null) return
    if (document.activeElement !== null && document.activeElement !== document.body) return
    // Compared rather than put in a selector: a style group's name can hold any character.
    const buttons = [...(container.current?.querySelectorAll<HTMLElement>('[data-opener]') ?? [])]
    for (const wanted of [closed, ...fallbacks]) {
      const button = buttons.find(candidate => candidate.dataset.opener === wanted)
      if (button) {
        button.focus()
        return
      }
    }
  }, [opener])

  return {
    editor,
    open(kind: TokenKind, current: string | null, slot: string | null, fields: Record<string, string>) {
      setAsking(null)
      setRefusal(null)
      setEditor(openTokenEditor(kind, current, slot, fields))
    },
    close() {
      setEditor(null)
    },
    // Typing in a field answers its own refusal and the form's, never another field's.
    setField(key: string, value: string) {
      setEditor(current => (current === null ? null : {
        ...current,
        fields: { ...current.fields, [key]: value },
        errors: Object.fromEntries(Object.entries(current.errors).filter(([field]) => field !== key && field !== FORM_ERROR)),
      }))
    },
    isAsking: (kind: TokenKind, name: string) => sameEntry(asking, kind, name),
    refusalOf: (kind: TokenKind, name: string) => (refusal !== null && sameEntry(refusal.ref, kind, name) ? refusal.message : null),
    ask(ref: TokenRef) {
      setRefusal(null)
      setAsking(ref)
    },
    keep() {
      setAsking(null)
    },
    /** Saves the open editor; a refusal stays in the form, each message at the one of `fields` it names. */
    async save(fields: readonly string[]) {
      if (editor === null) return
      const outcome = await onSave(editorPatch(editor))
      if (outcome === null) return
      if (outcome.ok) {
        returnTo.current = [openerOf('change', editor.kind, editorName(editor)), openerOf('add', editor.kind, editor.slot), openerOf('add', editor.kind, null)]
        setEditor(null)
      } else {
        setEditor(current => (current === null ? null : { ...current, errors: editorErrors(outcome, fields) }))
      }
    },
    // The question stays up until the write is back, so focus moves once the row is gone rather than while it is there.
    async remove(ref: TokenRef) {
      const outcome = await onSave(removalPatch(ref))
      if (outcome?.ok) returnTo.current = [openerOf('add', ref.kind, ref.slot), openerOf('add', ref.kind, null)]
      if (outcome !== null && !outcome.ok) setRefusal({ ref, message: editorErrors(outcome, [])[FORM_ERROR] })
      setAsking(null)
    },
  }
}
